import type { NsConnectionConfig, RoundingRule } from '@worklogix/shared';

import type { UpdateNsConnectionInput } from './api';

/**
 * Pure, React-free helpers for the NS Connection admin form (plan-v8 WEB #1).
 *
 * The form edits ONLY the non-secret {@link NsConnectionConfig} fields plus the
 * human write gate (`postingEnabled`). Secret NAME references are shown
 * read-only elsewhere and never travel through this form; secret VALUES are
 * never requested or displayed anywhere in web-app. Everything here is a pure
 * value transform so it is trivially unit-testable without a DOM.
 */

/** Connection seam mode options, in display order. */
export const CONNECTION_MODES: ReadonlyArray<{ value: 'local' | 'netsuite'; label: string }> = [
  { value: 'local', label: 'Local (synthetic — no NetSuite writes)' },
  { value: 'netsuite', label: 'NetSuite (real TimeBill posting)' },
];

/** Rounding-rule options, in display order. */
export const ROUNDING_RULES: ReadonlyArray<{ value: RoundingRule; label: string }> = [
  { value: 'exact', label: 'Exact (raw duration)' },
  { value: 'nearest_15', label: 'Nearest 15 minutes' },
  { value: 'nearest_6', label: 'Nearest 6 minutes' },
];

/** Editable, all-string form state (empty string = "unset"). */
export interface ConnectionFormState {
  mode: 'local' | 'netsuite';
  postingEnabled: boolean;
  account: string;
  restHost: string;
  subsidiaryId: string;
  defaultLocationId: string;
  defaultPayrollItemId: string;
  customForm: string;
  timeType: string;
  roundingRule: RoundingRule;
  payrollTimezone: string;
  allowDefaultPayrollItem: boolean;
}

/** Per-field validation messages; a field is absent when valid. */
export interface ConnectionFormErrors {
  postingEnabled?: string;
  account?: string;
  restHost?: string;
}

/** Build editable form state from a loaded config (nullish → empty string). */
export function toFormState(config: NsConnectionConfig): ConnectionFormState {
  return {
    mode: config.mode,
    postingEnabled: config.postingEnabled,
    account: config.account ?? '',
    restHost: config.restHost ?? '',
    subsidiaryId: config.subsidiaryId ?? '',
    defaultLocationId: config.defaultLocationId ?? '',
    defaultPayrollItemId: config.defaultPayrollItemId ?? '',
    customForm: config.customForm ?? '',
    timeType: config.timeType ?? '',
    roundingRule: config.roundingRule,
    payrollTimezone: config.payrollTimezone,
    allowDefaultPayrollItem: config.allowDefaultPayrollItem,
  };
}

/**
 * Validate the form. The write gate is the sensitive one: posting can only be
 * enabled in NetSuite mode, and NetSuite mode needs an account + REST host
 * before it can post anything real.
 */
export function validateConnectionForm(state: ConnectionFormState): ConnectionFormErrors {
  const errors: ConnectionFormErrors = {};

  if (state.postingEnabled && state.mode !== 'netsuite') {
    errors.postingEnabled = 'Posting can only be enabled while the mode is NetSuite.';
  }

  if (state.mode === 'netsuite') {
    if (state.account.trim() === '') {
      errors.account = 'Account is required for NetSuite mode.';
    }
    if (state.restHost.trim() === '') {
      errors.restHost = 'REST host is required for NetSuite mode.';
    }
  }

  return errors;
}

/** True when the form carries no blocking validation error. */
export function isConnectionFormValid(errors: ConnectionFormErrors): boolean {
  return Object.keys(errors).length === 0;
}

/**
 * True when the write gate is a *deliberate, safe* flip: it may only be ON when
 * the mode is NetSuite and the minimum fields are present. Drives the UI's
 * enable/disable of the gate switch (the server re-checks authoritatively).
 */
export function canEnablePosting(state: ConnectionFormState): boolean {
  return state.mode === 'netsuite' && state.account.trim() !== '' && state.restHost.trim() !== '';
}

/**
 * Build the `PUT` payload from form state: trim strings, and OMIT empties (as
 * absent optionals, per `exactOptionalPropertyTypes`) rather than sending `''`.
 * `mode`, `postingEnabled` and `roundingRule` are always sent.
 */
export function toUpdateInput(state: ConnectionFormState): UpdateNsConnectionInput {
  const input: UpdateNsConnectionInput = {
    mode: state.mode,
    postingEnabled: state.postingEnabled,
    roundingRule: state.roundingRule,
    allowDefaultPayrollItem: state.allowDefaultPayrollItem,
  };
  const payrollTimezone = state.payrollTimezone.trim();
  if (payrollTimezone !== '') {
    input.payrollTimezone = payrollTimezone;
  }
  const account = state.account.trim();
  if (account !== '') {
    input.account = account;
  }
  const restHost = state.restHost.trim();
  if (restHost !== '') {
    input.restHost = restHost;
  }
  const subsidiaryId = state.subsidiaryId.trim();
  if (subsidiaryId !== '') {
    input.subsidiaryId = subsidiaryId;
  }
  const defaultLocationId = state.defaultLocationId.trim();
  if (defaultLocationId !== '') {
    input.defaultLocationId = defaultLocationId;
  }
  const defaultPayrollItemId = state.defaultPayrollItemId.trim();
  if (defaultPayrollItemId !== '') {
    input.defaultPayrollItemId = defaultPayrollItemId;
  }
  const customForm = state.customForm.trim();
  if (customForm !== '') {
    input.customForm = customForm;
  }
  const timeType = state.timeType.trim();
  if (timeType !== '') {
    input.timeType = timeType;
  }
  return input;
}
